import React, { useEffect, useMemo, useState } from "react";
import { omit } from "lodash-es";

import { Card } from "shared/components";
import { GuestlistBlockForm } from "Bands/components";
import { copy } from "shared/assets";
import {
  BAND_ACTIONS,
  EventBlocks,
  IGuestlistBlockParams,
  IGuestlistBlockStatus,
  IGuestlistBlockType,
  IGuestlistTicketAndPassParams,
} from "Bands/types";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { Guests } from "shared/assets/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  clearActionMeta,
  createEventGuestlistBlock,
  dataMetaSelector,
  dispatchGetEventGuestlistBlocks,
} from "Bands/state";
import { API_ENDPOINTS } from "shared/constants";
import { useEventContext } from "Bands/contexts";
import { useClearActionMetaOnUnMount, useEventRoute } from "Bands/utils";
import { addTemporaryToast } from "shared/components/Toast";
import { BAND_PATHS } from "Bands/router";
import { Spacer } from "shared/layout";
import { CreateTicketAndPass } from "./CreateTicketAndPass";
import CopyBlockModal from "Bands/pages/copy-block/CopyBlockModal";

const { guestlistBlock: guestlistBlockCopy } = copy;

export const CreateGuestlistBlockForm: React.FC = () => {
  const dispatch = useDispatch();
  const { eventId } = useEventContext();
  const { pushRoute } = useEventRoute();

  const [ticketAndPasses, setTicketAndPasses] = useState<
    IGuestlistTicketAndPassParams[]
  >([]);

  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.createEventGuestlistBlock)
  );

  useClearActionMetaOnUnMount(BAND_ACTIONS.createEventGuestlistBlock);

  useEffect(() => {
    if (!eventId) return;

    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.createEventGuestlistBlock));

      dispatchGetEventGuestlistBlocks(dispatch, eventId);

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: guestlistBlockCopy.create.messages.success,
        })
      );
      // redirect to edit page
      pushRoute(BAND_PATHS.BAND_EVENT);
    }
  }, [apiMeta?.status, dispatch, eventId, pushRoute]);

  const defaultValues: IGuestlistBlockParams = useMemo(
    () => ({
      title: guestlistBlockCopy.title,
      status: IGuestlistBlockStatus.open,
      guestBlockType: IGuestlistBlockType.public,
      maxTicketsPerEntry: 1,
    }),
    []
  );

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        children: guestlistBlockCopy.create.buttons.create,
        isFullWidth: true,
        "data-testid": "guestlist_block_submit",
      },
    }),
    []
  );

  const handleSubmit = (data: IGuestlistBlockParams) => {
    if (!eventId) return;

    dispatch(
      createEventGuestlistBlock({
        ...data,
        ticketAndPassTypes: ticketAndPasses.map(
          (ticketAndPass) =>
            omit(ticketAndPass, "id") as IGuestlistTicketAndPassParams
        ),
        event: API_ENDPOINTS.EVENT(eventId),
      })
    );
  };

  return (
    <Card icon={Guests} title={guestlistBlockCopy.create.title}>
      <Spacer size="m">
        <CopyBlockModal blockType={EventBlocks.guestlist} />
        <GuestlistBlockForm
          hideStatus
          defaultValues={defaultValues}
          buttons={buttons}
          handleSubmit={handleSubmit}
          apiStatus={apiMeta?.status}
          apiError={apiMeta?.error}
          error={!!apiMeta?.error}
          preventSubmit={!ticketAndPasses.length}
        >
          <CreateTicketAndPass
            ticketAndPasses={ticketAndPasses}
            setTicketAndPasses={setTicketAndPasses}
          />
        </GuestlistBlockForm>
      </Spacer>
    </Card>
  );
};
